import React from 'react';
import {
    Router,
    Route,
    Switch,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { createMemoryHistory } from 'history';
import loadable from '@loadable/component';
import Layout from './Core/Layout';
import Error404 from './Pages/ErrorPages/Error404/Error404';
import {
    isUserLoggedIn,
    getOddsFormat,
    getCountryIso,
    onUserModeChange,
    onOddsFormatChange,
    getCustomerId,
    onRegulationTypeIdChange,
    getRegulationTypeId,
} from '../shared/utils/user';
import './styles/spa-testing-only.scss';
import { NAVIGATE_TO_EVENT_NAME, NAVIGATE_TO_NOTIFY_SYSTEMURL, getUrlContext } from './Utils/NavigationAdapter';
import {
    clearListeners,
    emit,
    on,
} from '../shared/utils/event';
import {
    clientInitSucceeded,
} from './Utils/WrapperIntegration/Ucf';
import ScrollToTop from './Components/ScrollToTop';

import { WEBSOCKET_CLOSE_REASON_REG_TYPE_ID_CHANGE, getWsInterface } from './Utils/Websockets';
import {
    getCompetitionOnlySlugs,
    getMultiGroupedOnlySlugs,
 } from '~spa/Utils/SiteConfig';
import { pushToDataLayer } from '~shared/utils/google-tag-manager';
import { clearVirtualGamesCache } from '~shared/services/virtuals';

const Homepage = loadable(
    () => import(/* webpackChunkName: "homepage" */ './Pages/Homepage/Homepage')
);
const BrightBundlesPage = loadable(
    () => import(/* webpackChunkName: "brightbundles" */ './Pages/Brightbundles/Brightbundles')
);
const BetFeedPage = loadable(
    () => import(/* webpackChunkName: "betfeedpage" */ './Pages/BetFeed/BetFeed')
);
const Football = loadable(
    () => import(/* webpackChunkName: "football" */ './Pages/Football/Football')
);
const Racing = loadable(
    () => import(/* webpackChunkName: "racing" */ './Pages/Racing/Racing')
);
const SportGeneric = loadable(
    () => import(/* webpackChunkName: "sportgeneric" */ './Pages/SportGeneric/SportGeneric')
);
const Inplay = loadable(
    () => import(/* webpackChunkName: "inplay" */ './Pages/Inplay/Inplay')
);
const VirtualSportsContainer = loadable(
    () => import(/* webpackChunkName: "virtual-sports" */ './Pages/VirtualSports/VirtualSportsContainer')
);
const Recommendations = loadable(
    () => import(/* webpackChunkName: "recommendations" */ './Pages/Recommendations/Recommendations')
);
const BetHistory = loadable(
    () => import(/* webpackChunkName: "bethistory-page" */ './Pages/BetHistory/BetHistory')
);
const Golf = loadable(
    () => import(/* webpackChunkName: "golf" */ './Pages/Golf/Golf')
);
const MySportsOffers = loadable(
    () => import(/* webpackChunkName: "mysportsoffers" */ './Pages/MySportsOffers/MySportsOffers')
);
const SpecialsPage = loadable(
    () => import(/* webpackChunkName: "specialspage" */ './Pages/Specials/Specials')
);
const SportcallerPage = loadable(
    () => import(/* webpackChunkName: "sportcallerpage" */ './Pages/Sportcaller/Sportcaller')
);

class Spa extends React.Component {
    constructor(props) {
        super(props);

        const {
            systemUrlPath: initialUrlPath,
            contextId,
        } = props.initialContext;

        const [initialPathname, initialSearch] = initialUrlPath.split('?');

        this.memoryHistory = createMemoryHistory({
            initialEntries: [{
                pathname: initialPathname,
                search: initialSearch,
                state: {
                    contextId,
                    context: {...props.initialContext},
                },
            }],
        });

        on(NAVIGATE_TO_EVENT_NAME, async (url) => {
            const context = await getUrlContext(url);

            this.memoryHistory.push(context.systemUrlPath, {
                contextId: context.contextId,
                context: {...context},
            });

            this.setState({
                currentUrlPath: context.systemUrlPath,
            });

            emit(NAVIGATE_TO_NOTIFY_SYSTEMURL, context.systemUrlPath);
        });

        this.state = {
            isLoggedIn: isUserLoggedIn(),
            oddsFormat: getOddsFormat(),
            country: getCountryIso(),
            currentUrlPath: initialUrlPath,
            lastRegulationTypeId: getRegulationTypeId(),
        };

        onUserModeChange(
            (key, isLoggedIn) => {
                this.setState({
                    isLoggedIn: isLoggedIn,
                    oddsFormat: getOddsFormat(),
                    country: getCountryIso(),
                });
            }
        );

        onRegulationTypeIdChange(() => {
            const newRegulationTypeId = getRegulationTypeId();
            const { lastRegulationTypeId } = this.state;
            if (parseInt(newRegulationTypeId, 10) !== parseInt(lastRegulationTypeId, 10)) {
                this.setState({
                    lastRegulationTypeId: newRegulationTypeId,
                });
                getWsInterface().softCloseAll(WEBSOCKET_CLOSE_REASON_REG_TYPE_ID_CHANGE);
                clearVirtualGamesCache();
            }
        });

        onOddsFormatChange(
            (key, oddsFormat) => this.setState({
                oddsFormat,
            })
        );

        // If user is logged in then push to userId during page load
        if (isUserLoggedIn()) {
            pushToDataLayer({ event: 'SP_USERID_PAGELOAD', 'userId': getCustomerId() });
        }
    }

    componentDidMount() {
        clientInitSucceeded();
    }

    componentWillUnmount() {
        clearListeners(NAVIGATE_TO_EVENT_NAME);
        getWsInterface().closeAllConnections();
    }

    render() {
        const {
            isLoggedIn,
            oddsFormat,
            country,
        } = this.state;
        const layoutKey = `${isLoggedIn}-${oddsFormat}`;
        const COMPETITIONS_ONLY_SPORTS = getCompetitionOnlySlugs();
        const MULTI_GROUPING_ONLY_SPORTS = getMultiGroupedOnlySlugs();

        return (
            <Router history={this.memoryHistory}>
                <ScrollToTop />
                <Layout {...this.state} key={layoutKey} history={this.memoryHistory}>
                    <Switch>
                        <Route path="/" exact render={
                            () => <Homepage isLoggedIn={isLoggedIn} />
                        }/>
                        <Route path="/betbuilder" component={BrightBundlesPage} />
                        <Route path="/betfeed" render={
                            () => <BetFeedPage isLoggedIn={isLoggedIn} />
                        } />
                        <Route path="/inplay" component={Inplay} />
                        <Route path="/virtual-sports/:gameSlug" render={
                            (props) => <VirtualSportsContainer selectedGameSlug={props.match.params.gameSlug}/>
                        } />
                        <Route path="/virtual-sports" component={VirtualSportsContainer} />
                        <Route path="/recommendations" render={
                            () => <Recommendations isLoggedIn={isLoggedIn} country={country} />
                        }/>
                        <Route path="/bets" render={
                            (props) => <BetHistory isLoggedIn={isLoggedIn} location={props.location} />
                        } />
                        <Route path="/my-rewards" render={
                            () => <MySportsOffers isLoggedIn={isLoggedIn} />
                        } />
                        <Route path="/888-special" component={() =>
                            <SpecialsPage isLoggedIn={isLoggedIn}/>
                        } />
                        <Route path="/10kdrive/tcs" component={() =>
                            <SportcallerPage tcs={true} />
                        } />
                        <Route path="/10kdrive" component={() =>
                            <SportcallerPage />
                        } />
                        <Route path="/:sportSlug(football)" component={Football} />
                        <Route path="/:sportSlug(golf)" component={Golf} />
                        <Route path="/:sportSlug(horse-racing|greyhound-racing|trotting)" render={
                            (props) => <Racing key={props.match.params.sportSlug} {...props} />
                        } />
                        <Route path={`/:sportSlug(${COMPETITIONS_ONLY_SPORTS.join('|')})`} render={
                            (props) => <SportGeneric key={props.match.params.sportSlug} {...props} isCompetitionsOnly />
                        } />
                        <Route path={`/:sportSlug(${MULTI_GROUPING_ONLY_SPORTS.join('|')})`} render={
                            (props) => <SportGeneric key={props.match.params.sportSlug} {...props} isMultiGrouping />
                        } />
                        <Route path="/:sportSlug" render={
                            (props) => <SportGeneric key={props.match.params.sportSlug} {...props} />
                        } />
                        <Route component={Error404} />
                    </Switch>
                </Layout>
            </Router>
        );
    }
}

Spa.propTypes = {
    initialContext: PropTypes.object.isRequired,
};

export default Spa;
